<script lang="ts">
    import { AttributesSectionEditorViewModel } from "@Alternatives/Attributes/Components/AttributesSectionEditor";
    import AttributesSectionEditor from '@Alternatives/Attributes/Components/AttributesSectionEditor.vue';
    import { dateCompare1, generateName, strCompareLowerCase } from '@common/Common';
    import CorporateNavigation from '@common/Components/CorporateNavigation.vue';
    import { ICommandStatus } from "@cqrs/cqrs";
    import { AttributeEditorDialogViewModel, SaveDateAttributeEventPayload, SaveEmailAttributeEventPayload, SaveMultilineAttributeEventPayload, SaveNumericAttributeEventPayload, SaveTextAttributeEventPayload, TestScriptEventPayload } from "@Quest/CorporateAttributes/Components/AttributeEditorDialog";
    import AttributeEditorDialog from '@Quest/CorporateAttributes/Components/AttributeEditorDialog.vue';
    import { CreateCorporateDateAttributeCommandV1, CreateCorporateEmailAttributeCommandV1, CreateCorporateMultilineAttributeCommandV1, CreateCorporateNumericAttributeCommandV1, CreateCorporateTextAttributeCommandV1, DeleteCorporateAttributeCommandV1, RenameCorporateAttributesSectionCommandV1, UpdateCorporateDateAttributeCommandV1, UpdateCorporateEmailAttributeCommandV1, UpdateCorporateMultilineAttributeCommandV1, UpdateCorporateNumericAttributeCommandV1, UpdateCorporateTextAttributeCommandV1 } from "@Quest/CorporateAttributes/Domain/commands-core";
    import CorporateAttributesCommands from "@Quest/CorporateAttributes/Domain/commands-vue-component";
    import CorporateAttributesQueries from "@Quest/CorporateAttributes/Domain/queries-vue-component";
    import { ManageAllPageReadModel, AttributeEditorReadModel, AttributeReadModel, CalcScriptResultReadModel } from "@Quest/CorporateAttributes/readmodels";
    import { ICommandBase } from '@Quest/cqrs';
    import moment from "moment";
    import { Component, mixins } from 'vue-facing-decorator';

    @Component({
        components: {
            CorporateNavigation, AttributesSectionEditor, AttributeEditorDialog
        }
    })
    export default class ManageAllPage extends mixins(CorporateAttributesQueries, CorporateAttributesCommands)
    {
        get workspaceId(): number { return Number(this.$route?.params?.workspaceId ?? 0); }
        get strategyId(): string { return this.$route?.params?.strategyId ?? ''; }

        rm: ManageAllPageReadModel | null = null;

        get alternativesListId(): number { return this.rm!.AlternativesListId; }
        get tchProjectId(): number { return this.rm!.TchProjectId; }
        get attributes() { return this.rm?.Attributes ?? []; }

        searchPhrase: string | null = null;
        collapseAll: boolean = true;
        sections: SectionVM[] = [];

        attributeEditorDialog: AttributeEditorDialogViewModel = new AttributeEditorDialogViewModel();
        attributesSectionEditor = new AttributesSectionEditorViewModel();

        get sectionsView(): SectionVM[] 
        {
            return this.sections.sort((a, b) => strCompareLowerCase(a.Name, b.Name));
        }

        getModifiedDateStr(rm: AttributeReadModel): string 
        {
            return moment(rm.Modified).format('MMM D, YYYY');
        }

        getModifiedTimeStr(rm: AttributeReadModel): string
        {
            return moment(rm.Modified).format('LT');
        }

        buildSections() 
        {
            // Create a map to hold sections by their names
            const sectionsMap = new Map();

            // Iterate over attributes and populate the map
            // This automatically ensures that each section name is unique
            this.attributes.forEach(attribute =>
            {
                if (!sectionsMap.has(attribute.Section))
                {
                    sectionsMap.set(attribute.Section, new SectionVM(this, attribute.Section));
                }
            });

            // Replace the Sections array with new values from the map
            // This filters out empty sections and adds new ones
            this.sections = Array.from(sectionsMap.values());
        }

        toggleExpandAll()
        {
            if (this.collapseAll === true)
            {
                this.sections.forEach(s => s.Expanded = false);
            }
            else
            {
                this.sections.forEach(s => s.Expanded = true);
            }

            this.collapseAll = !this.collapseAll;
        }



        showAttributeCreator(section: string | null)
        {
            this.attributeEditorDialog.showCreator({
                defaultName: generateName("Attribute"),
                defaultSection: section,

                allowChangeAttributeType: true,
                allowInputTypeEvaluate: true,
                allowMultipleValuesEnabled: true,
            });

            this.runAttributeEditorQueryV1(this.workspaceId, this.tchProjectId, null);
        }

        showAttributeCloner(rm: AttributeReadModel)
        {
            this.attributeEditorDialog.showCloner(rm.Id, rm.Name);
            this.runAttributeEditorQueryV1(this.workspaceId, this.tchProjectId, rm.Id);
        }

        showAttributeEditor(rm: AttributeReadModel)
        {
            this.attributeEditorDialog.showEditor(rm.Id, rm.Name);
            this.runAttributeEditorQueryV1(this.workspaceId, this.tchProjectId, rm.Id);
        }


        showAttributesSectionEditor(s: SectionVM)
        {
            this.attributesSectionEditor.show(s.Name)
        }



        handleTestAttributeScript(e: TestScriptEventPayload)
        {
            this.runCalcAttributeScriptTestQueryV1(this.workspaceId, this.tchProjectId, this.alternativesListId, e.Script, e.ScriptTestInputs);
        }

        handleSaveNumericAttribute(e: SaveNumericAttributeEventPayload)
        {
            if (e.Id === 0)
            {
                this.invokeCreateCorporateNumericAttributeCommandV1(this.workspaceId, this.strategyId, 0,
                    e.Name, e.Description, e.Section, e.InputType, e.AllowMultipleValues, e.MinValue, e.MaxValue, e.Script, e.DefinedValues);
            }
            else
            {
                this.invokeUpdateCorporateNumericAttributeCommandV1(this.workspaceId, this.strategyId, e.Id,
                    e.Name, e.Description, e.Section, e.InputType, e.AllowMultipleValues, e.MinValue, e.MaxValue, e.Script, e.DefinedValues);
            }
        }

        handleSaveDateAttribute(e: SaveDateAttributeEventPayload)
        {
            if (e.Id === 0)
            {
                this.invokeCreateCorporateDateAttributeCommandV1(this.workspaceId, this.strategyId, 0,
                    e.Name, e.Description, e.Section, e.InputType, e.AllowMultipleValues, e.MinValue, e.MaxValue, e.DefinedValues);
            }
            else
            {
                this.invokeUpdateCorporateDateAttributeCommandV1(this.workspaceId, this.strategyId, e.Id,
                    e.Name, e.Description, e.Section, e.InputType, e.AllowMultipleValues, e.MinValue, e.MaxValue, e.DefinedValues);
            }
        }

        handleSaveEmailAttribute(e: SaveEmailAttributeEventPayload)
        {
            if (e.Id === 0)
            {
                this.invokeCreateCorporateEmailAttributeCommandV1(this.workspaceId, this.strategyId, 0,
                    e.Name, e.Description, e.Section, e.InputType, e.AllowMultipleValues, e.DefinedValues);
            }
            else
            {
                this.invokeUpdateCorporateEmailAttributeCommandV1(this.workspaceId, this.strategyId, e.Id,
                    e.Name, e.Description, e.Section, e.InputType, e.AllowMultipleValues, e.DefinedValues);
            }
        }

        handleSaveTextAttribute(e: SaveTextAttributeEventPayload)
        {
            if (e.Id === 0)
            {
                this.invokeCreateCorporateTextAttributeCommandV1(this.workspaceId, this.strategyId, 0,
                    e.Name, e.Description, e.Section, e.InputType, e.AllowMultipleValues, e.MinLength, e.MaxLength, e.DefinedValues);
            }
            else
            {
                this.invokeUpdateCorporateTextAttributeCommandV1(this.workspaceId, this.strategyId, e.Id,
                    e.Name, e.Description, e.Section, e.InputType, e.AllowMultipleValues, e.MinLength, e.MaxLength, e.DefinedValues);
            }
        }

        handleSaveMultilineAttribute(e: SaveMultilineAttributeEventPayload)
        {
            if (e.Id === 0)
            {
                this.invokeCreateCorporateMultilineAttributeCommandV1(this.workspaceId, this.strategyId, 0,
                    e.Name, e.Description, e.Section, e.Rows, e.MinLength, e.MaxLength);
            }
            else
            {
                this.invokeUpdateCorporateMultilineAttributeCommandV1(this.workspaceId, this.strategyId, e.Id,
                    e.Name, e.Description, e.Section, e.Rows, e.MinLength, e.MaxLength);
            }
        }



        handleDeleteCorporateAttributeCommand(attrId: number)
        {
            if (confirm("Are you sure?") == true)
            {
                this.invokeDeleteCorporateAttributeCommandV1(this.workspaceId, this.strategyId, attrId, true);
            }
        }

        handleRenameAttributesSectionCommand(newName: string | null, oldName: string | null)
        {
            this.invokeRenameCorporateAttributesSectionCommandV1(this.workspaceId, this.strategyId, oldName, newName);
        }

        mounted()
        {
            document.title = "Enterprise Attributes | TransparentChoice";
            this.runManageAllPageQueryV1(this.workspaceId, this.strategyId);
        }

        onSetModel(model: any, modelName: string) 
        {
            if (modelName === "ManageAllPageReadModel")
            {
                this.rm = model as ManageAllPageReadModel;
                this.buildSections();
            }
            else if (modelName === "AttributeReadModel")
            {
                const rm = model as AttributeReadModel;
                const curr = this.attributes.find(a => a.Id === rm.Id);

                if (curr != null)
                {
                    this.attributes.remove(curr);
                }

                this.attributes.push(rm);
                this.buildSections();

                this.attributeEditorDialog.close();
            }
            else if (modelName === "AttributeEditorReadModel")
            {
                const rm = model as AttributeEditorReadModel;
                this.attributeEditorDialog.setData(rm);
            }
            else if (modelName === "CalcScriptResultReadModel")
            {
                this.attributeEditorDialog.setScriptTestResult(model as CalcScriptResultReadModel);
            }
        }

        onCommandSucceed(cmd: ICommandBase, correlationId: string, result: any)
        {
            if (DeleteCorporateAttributeCommandV1.matchType(cmd))
            {
                this.rm.Attributes = this.rm.Attributes.filter(a => a.Id !== Number(correlationId));
                this.buildSections();
            }
            else if (RenameCorporateAttributesSectionCommandV1.matchType(cmd))
            {
                const cmd1 = cmd as RenameCorporateAttributesSectionCommandV1;

                const section = this.sections.find(s => s.Name === cmd1.OldName);

                if (section)
                {
                    section.attributes.forEach(a => a.Section = cmd1.NewName);
                    this.buildSections();
                }

                this.attributesSectionEditor.close();
            }

            else if (CreateCorporateNumericAttributeCommandV1.matchType(cmd) ||
                CreateCorporateDateAttributeCommandV1.matchType(cmd) ||
                CreateCorporateEmailAttributeCommandV1.matchType(cmd) ||
                CreateCorporateTextAttributeCommandV1.matchType(cmd) ||
                CreateCorporateMultilineAttributeCommandV1.matchType(cmd))        
            {
                const attrId = Number(result);
                this.runAttributeByIdQueryV1(this.workspaceId, this.tchProjectId, this.alternativesListId, attrId);
            }
            else if (UpdateCorporateNumericAttributeCommandV1.matchType(cmd) ||
                UpdateCorporateDateAttributeCommandV1.matchType(cmd) ||
                UpdateCorporateEmailAttributeCommandV1.matchType(cmd) ||
                UpdateCorporateTextAttributeCommandV1.matchType(cmd) ||
                UpdateCorporateMultilineAttributeCommandV1.matchType(cmd))
            {
                const attrId = Number(correlationId);
                this.runAttributeByIdQueryV1(this.workspaceId, this.tchProjectId, this.alternativesListId, attrId);
            }
        }

        onCommandFailed(status: ICommandStatus)
        {
            if (CreateCorporateNumericAttributeCommandV1.matchType(status.Command) ||
                CreateCorporateDateAttributeCommandV1.matchType(status.Command) ||
                CreateCorporateEmailAttributeCommandV1.matchType(status.Command) ||
                CreateCorporateTextAttributeCommandV1.matchType(status.Command) ||
                CreateCorporateMultilineAttributeCommandV1.matchType(status.Command))        
            {
                this.attributeEditorDialog.displayValidationErrors(status.ValidationErrors);
            }
            else if (UpdateCorporateNumericAttributeCommandV1.matchType(status.Command) ||
                UpdateCorporateDateAttributeCommandV1.matchType(status.Command) ||
                UpdateCorporateEmailAttributeCommandV1.matchType(status.Command) ||
                UpdateCorporateTextAttributeCommandV1.matchType(status.Command) ||
                UpdateCorporateMultilineAttributeCommandV1.matchType(status.Command))
            {
                this.attributeEditorDialog.displayValidationErrors(status.ValidationErrors);
            }
            else if (DeleteCorporateAttributeCommandV1.matchType(status.Command))
            {
                this.attributeEditorDialog.displayValidationErrors(status.ValidationErrors);
            }
        }
    }

    class SectionVM
    {
        page: ManageAllPage = null;

        Name: string;
        Expanded: boolean = true;
        OrderBy: string = "name";
        OrderByDescending: boolean = false;

        get attributes() { return this.page.attributes?.filter(a => a.Section?.toLowerCase() === this.Name?.toLowerCase()) ?? []; }

        get AttributesView()
        {
            let view = new Array(...this.attributes);

            if (this.page.searchPhrase)
            {
                view = view.filter(o => o.Name.toLowerCase().indexOf(this.page.searchPhrase.toLowerCase()) >= 0);
            }

            if (this.OrderBy === "name")
            {
                view = view.sort((a, b) => strCompareLowerCase(a.Name, b.Name));
            }
            else if (this.OrderBy === "modified")
            {
                view = view.sort((a, b) => dateCompare1(a.Modified, b.Modified));
            }

            if (this.OrderByDescending === true)
            {
                view = view.reverse();
            }

            return view;
        }

        constructor(page: ManageAllPage, name: string)
        {
            this.page = page;
            this.Name = name;
        }

        orderByName()
        {
            if (this.OrderBy === 'name')
            {
                this.OrderByDescending = !this.OrderByDescending;
            }
            else
            {
                this.OrderByDescending = false;
                this.OrderBy = 'name';
            }
        }

        orderByModified()
        {
            if (this.OrderBy === 'modified')
            {
                this.OrderByDescending = !this.OrderByDescending;
            }
            else
            {
                this.OrderByDescending = true;
                this.OrderBy = 'modified';
            }
        }
    }
</script>

<template>
    <div>
        <AttributeEditorDialog :viewModel="attributeEditorDialog"
                               @test-script="handleTestAttributeScript"
                               @save-numeric="handleSaveNumericAttribute"
                               @save-date="handleSaveDateAttribute"
                               @save-text="handleSaveTextAttribute" 
                               @save-email="handleSaveEmailAttribute" 
                               @save-multiline="handleSaveMultilineAttribute"/>

        <AttributesSectionEditor :viewModel="attributesSectionEditor"
                                 @update-section-name="handleRenameAttributesSectionCommand" />

        <CorporateNavigation ActivePage="metrics" />

        <div v-if="rm" class="container" style="margin-top: 80px; padding-top: 40px;">
            <div class="row">
                <div class="col-lg-6 d-flex align-items-center">
                    <h1 style="font-size: 2em; font-weight: 300; color: #33475b; margin: 0;">Attributes</h1>
                </div>
                <div class="col-lg-6 text-right align-self-end">
                    <button role="button" class="btn btn-outline-secondary mr-2" style="padding: 0.7em 2em;" @click="toggleExpandAll">Collapse / Expand all</button>

                    <button role="button" class="btn btn-success mr-2" style="padding: 0.7em 2em;" @click="showAttributeCreator()">Create attribute</button>

                    <a class="btn btn-outline-secondary btn-lg" href="https://www.transparentchoice.com/user-guide/attributes" title="User guide: Configure attributes" target="_blank">
                        <i class="fa fa-info" aria-hidden="true"></i>
                    </a>
                </div>
            </div>
            <hr style="border-color: #bbb; margin: 20px 0 60px;" />

            <div class="row">
                <div class="col-lg-4 offset-lg-8">
                    <div class="input-group input-group-lg">
                        <input type="search" placeholder="Search for an attribute" class="form-control" v-model="searchPhrase">
                        <span class="input-group-append">
                            <span class="input-group-text"><i class="fa-solid fa-magnifying-glass" aria-hidden="true"></i></span>
                        </span>
                    </div>
                </div>
            </div>


            <div style="margin-top: 50px;" v-for="s in sectionsView">
                <div>
                    <h4 class="pull-left" style="margin: 0; padding: 0;">
                        <a class="text-dark" @click="s.Expanded = !s.Expanded">
                            <i class="fa mr-1" :class="{'fa-angle-right': !s.Expanded, 'fa-angle-down': s.Expanded}"></i>
                            <span v-if="s.Name" style="color: black !important;">{{s.Name}}</span>
                            <span v-if="!s.Name" style="color: black !important; font-style: italic;">Unnamed</span>
                        </a>
                    </h4>
                    <div class="pull-right">
                        <a class="text-dark" @click="showAttributesSectionEditor(s)">Edit section name</a> | <a class="text-dark" @click="showAttributeCreator(s.Name)">Create attribute</a>
                    </div>
                    <div class="clearfix"></div>
                </div>
                <div v-if="s.Expanded">
                    <div>
                        <div style="margin-top: 20px; overflow-y: visible;">

                            <table class="table table-bordered table-hover table-browser" style="margin-bottom: 0 !important;">
                                <thead>
                                    <tr>
                                        <th class="sortable" @click="s.orderByName()" :class="{ 'selected': s.OrderBy === 'name' }">
                                            <span class="text-uppercase font-weight-normal">Name</span>
                                            <span class="fa-stack">
                                                <i class="fa fa-sort-asc fa-stack-1x" :class="{ 'selected': s.OrderBy === 'name' && s.OrderByDescending === false }"></i>
                                                <i class="fa fa-sort-desc fa-stack-1x" :class="{ 'selected': s.OrderBy === 'name' && s.OrderByDescending === true }"></i>
                                            </span>
                                        </th>
                                        <th class="sortable text-center" style="width: 180px;" @click="s.orderByModified()" :class="{ 'selected': s.OrderBy === 'modified' }">
                                            <span class="text-uppercase font-weight-normal">Updated on</span>
                                            <span class="fa-stack">
                                                <i class="fa fa-sort-asc fa-stack-1x" :class="{ 'selected': s.OrderBy === 'modified' && s.OrderByDescending === false }"></i>
                                                <i class="fa fa-sort-desc fa-stack-1x" :class="{ 'selected': s.OrderBy === 'modified' && s.OrderByDescending === true }"></i>
                                            </span>
                                        </th>
                                        <th style="width: 90px;"></th>
                                    </tr>
                                </thead>

                                <tbody>
                                    <tr v-for="attr in s.AttributesView" :key="attr.Id" :class="{'bg-danger': attr.IsSystem }">
                                        <td>
                                            <h5>
                                                <a class="text-dark" @click="showAttributeEditor(attr)">{{attr.Name}}</a><br />
                                                <small>{{attr.InputTypeDisplayName}}</small>
                                            </h5>
                                        </td>
                                        <td class="text-center">
                                            <span>{{getModifiedDateStr(attr)}}</span><br />
                                            <small>{{getModifiedTimeStr(attr)}}</small>
                                        </td>
                                        <td>
                                            <div class="dropdown">
                                                <button type="button" class="btn btn-outline-secondary dropdown-toggle" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                                                    Actions
                                                </button>
                                                <div class="dropdown-menu dropdown-menu-right">
                                                    <a class="dropdown-item" @click="showAttributeEditor(attr)">Edit attribute</a>
                                                    <a class="dropdown-item" @click="showAttributeCloner(attr)">Clone attribute</a>
                                                    <div class="dropdown-divider"></div>
                                                    <a class="dropdown-item" @click="handleDeleteCorporateAttributeCommand(attr.Id)">Delete attribute</a>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>

                                </tbody>
                            </table>

                        </div>
                    </div>
                </div>
            </div>

        </div>
    </div>
</template>

<style scoped>

</style>
